// Loads saved clerk statements from the Render service and turns them into the row shape
// the Clerk Statements screen works with.
//
// These records are the authoritative outstanding-fees position: each one is a clerk's
// statement PDF that has been parsed into invoice rows, then annotated by the clerk and the
// solicitor. They are reached through /api/render/... so the request passes through the
// authenticated, signing proxy - never straight to Render.

import { parseStatementAmount, paymentStatusFromLabel } from "./clerkStatements.js";

export const CLERK_STATEMENTS_PATH = "/api/render/api/clerk-statements/records";
export const CLERK_STATEMENT_ROW_STATUS_PATH = "/api/render/api/clerk-statements/row-status";

// The service stores a record per label variant, so the same statement appears more than
// once - "Parnell's Barristers Pty Ltd" and "Parnell's Barristers Pty Ltd - VALS statement"
// are the same 77 rows saved twice. Showing both would double every total, so only the most
// recent record per clerk per firm is kept, which is what the operator dashboard does.
export function dedupeClerkRecords(records = []) {
  const newestByKey = new Map();
  for (const record of records) {
    const clerk = String(record?.clerk_name || record?.firm || "")
      .replace(/\s*-\s*(VALS|LACW)\s+statement\s*$/i, "")
      .trim()
      .toLowerCase();
    const key = `${record?.client_firm_label || ""}::${clerk}`;
    const existing = newestByKey.get(key);
    if (!existing || String(record?.created_at || "") > String(existing?.created_at || "")) {
      newestByKey.set(key, record);
    }
  }
  return [...newestByKey.values()];
}

// Row ids must be stable across reloads, or a reimbursement tick would detach from its
// invoice the moment the page refreshed. The invoice number is unique within a statement,
// and the record id identifies the statement.
export const clerkStatementRowId = (record, row, index) =>
  `clerk-${record?.id}-${String(row?.invoice_number || `row${index}`).trim()}`;

export function normaliseClerkRecord(record) {
  const rows = Array.isArray(record?.rows) ? record.rows : [];
  const clerkName = String(record?.clerk_name || record?.firm || "Unknown clerk")
    .replace(/\s*-\s*(VALS|LACW)\s+statement\s*$/i, "")
    .trim();

  return rows.map((row, index) => {
    const { amount, suspect } = parseStatementAmount(row?.outstanding_amount);
    // The clerk and the solicitor each annotate a row, and they often disagree - one says
    // "paid last week", the other "awaiting retro aid". Both are kept; neither is discarded
    // in favour of the other. They belong to the statement and are shown read-only.
    const statementNotes = [row?.notes, row?.clerk_comments, row?.solicitor_comments]
      .map((value) => String(value ?? "").trim())
      .filter(Boolean);

    return {
      id: clerkStatementRowId(record, row, index),
      sourceId: String(record?.id ?? ""),
      source: "clerk_statement",
      clientName: String(row?.client_name || "Unnamed client").trim(),
      counselName: String(row?.barrister || "Counsel not specified").trim(),
      clerkName,
      invoiceName: String(row?.invoice_number || "").trim() || "No invoice number",
      invoiceSize: 0,
      invoiceBase64: null,
      invoiceAmount: String(amount),
      amountSuspect: suspect,
      status: paymentStatusFromLabel(row?.status),
      // What the portal itself has recorded against this row, and the only note the screen
      // may overwrite. Kept apart from the clerk's and solicitor's own comments so saving a
      // payment note cannot swallow their commentary back into itself.
      notes: String(row?.payment_notes ?? "").trim(),
      statementNotes: [...new Set(statementNotes)].join(" — "),
      // The row is addressed by invoice number when the status is saved; the index is the
      // fallback for rows that came off a statement with no number at all.
      rowIndex: index,
      uploadedAt: record?.created_at || "",
      statementLabel: String(record?.statement_label || "").trim(),
      statementAsOf: String(record?.as_of || "").trim(),
      clientFirm: String(record?.client_firm_label || "").trim(),
      matterId: "",
    };
  });
}

// Brand on this screen is lowercase ("vals"); the records label the firm in caps ("VALS").
// Records with no label at all are older imports that predate the field - they are excluded
// rather than shown under an arbitrary firm, since attributing one firm's outstanding fees
// to another would be worse than omitting them.
export function selectClerkStatementsForBrand(records = [], brand = "") {
  const wanted = String(brand || "").trim().toUpperCase();
  return dedupeClerkRecords(records)
    .filter((record) => String(record?.client_firm_label || "").trim().toUpperCase() === wanted)
    .flatMap(normaliseClerkRecord);
}

export async function fetchClerkStatements(brand, fetchImpl = globalThis.fetch, limit = 50) {
  const response = await fetchImpl(`${CLERK_STATEMENTS_PATH}?limit=${encodeURIComponent(limit)}`, {
    credentials: "include",
    cache: "no-store",
  });
  if (!response.ok) throw new Error(`Clerk statements returned ${response.status}`);
  const payload = await response.json();
  if (payload?.ok === false) throw new Error(payload.error || "Clerk statements unavailable");
  return selectClerkStatementsForBrand(payload?.records || [], brand);
}

// Saves the portal's payment status and note against one row of a stored clerk statement.
//
// Fee slips from Deadline Guard PATCH their own record and uploaded counsel invoices are
// written back to vals_drafts, but a statement row had no save path at all: the screen fell
// through to the vals_drafts branch and read form_data off a draft that does not exist,
// which is where "Cannot read properties of undefined" came from.
export async function updateClerkStatementRowStatus(
  { recordId, invoiceNumber, rowIndex, statusLabel, paymentNotes } = {},
  fetchImpl = globalThis.fetch,
) {
  const response = await fetchImpl(CLERK_STATEMENT_ROW_STATUS_PATH, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      record_id: Number(recordId),
      invoice_number: String(invoiceNumber ?? "").trim(),
      row_index: Number.isInteger(rowIndex) ? rowIndex : -1,
      status: String(statusLabel ?? "").trim(),
      payment_notes: String(paymentNotes ?? ""),
    }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload?.ok === false) {
    throw new Error(payload?.error || `Saving the fee slip status returned ${response.status}`);
  }
  return payload;
}
